import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  TextField,
  Alert,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  VerifiedUser,
  Description,
  Home as HomeIcon,
  Schedule,
  CheckCircle,
  Info,
  AttachMoney,
  Timeline
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useScore } from '../context/ScoreContext';
import StarRating from '../components/Gamification/StarRating';

const VerificationPage: React.FC = () => {
  const navigate = useNavigate();
  const { state: scoreState, dispatch: scoreDispatch } = useScore();

  const [selectedOption, setSelectedOption] = useState<'paper' | 'visit' | ''>('');
  const [additionalNotes, setAdditionalNotes] = useState('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [preferredDate, setPreferredDate] = useState('');
  const [preferredTime, setPreferredTime] = useState('');

  const verificationOptions = [
    {
      id: 'paper' as const,
      title: 'Document Verification',
      description: 'Submit supporting documents for remote verification of your compliance score',
      icon: <Description />,
      duration: '3-5 business days',
      cost: '$149',
      features: [
        'Review submitted documentation',
        'Verify compliance certificates',
        'Check regulatory documents',
        'Digital verification badge',
        'Official compliance report'
      ]
    },
    {
      id: 'visit' as const,
      title: 'Property Inspection',
      description: 'Professional on-site inspection by certified compliance assessor',
      icon: <HomeIcon />,
      duration: '1-2 weeks',
      cost: '$299',
      features: [
        'Comprehensive site inspection',
        'Professional assessor visit',
        'Detailed compliance report',
        'Photo documentation',
        'Corrective action recommendations',
        'Premium verification badge'
      ]
    }
  ];

  const currentOption = verificationOptions.find(opt => opt.id === selectedOption);

  const handleSubmitVerification = () => {
    if (selectedOption) {
      scoreDispatch({
        type: 'SET_VERIFICATION_STATUS',
        payload: selectedOption === 'paper' ? 'paper' : 'visit'
      });
      setShowConfirmDialog(true);
    }
  };

  const handleConfirmSubmission = () => {
    setShowConfirmDialog(false);
    // In a real app, this would submit to backend
    navigate('/');
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Header */}
      <motion.div variants={itemVariants}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Avatar
            sx={{
              width: 80,
              height: 80,
              mx: 'auto',
              mb: 2,
              bgcolor: 'primary.main',
              boxShadow: '0 8px 32px rgba(25, 118, 210, 0.3)'
            }}
          >
            <VerifiedUser sx={{ fontSize: 40 }} />
          </Avatar>

          <Typography
            variant="h3"
            component="h1"
            sx={{ fontWeight: 'bold', mb: 2 }}
          >
            Verify Your Score
          </Typography>

          <Typography variant="h6" color="textSecondary" sx={{ maxWidth: 600, mx: 'auto' }}>
            Make your compliance score official with our professional verification services
          </Typography>
        </Box>
      </motion.div>

      {/* Current Score Summary */}
      <motion.div variants={itemVariants}>
        <Card sx={{ mb: 4, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
          <CardContent sx={{ p: 3 }}>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={4}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {scoreState.totalScore}
                  </Typography>
                  <Typography variant="body1" sx={{ opacity: 0.9 }}>
                    Current Score
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ textAlign: 'center' }}>
                  <StarRating rating={scoreState.starRating} size="large" />
                  <Typography variant="body1" sx={{ mt: 1, opacity: 0.9 }}>
                    {scoreState.complianceLevel?.name}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ textAlign: 'center' }}>
                  <Chip
                    label="UNVERIFIED"
                    sx={{
                      bgcolor: 'rgba(255,193,7,0.3)',
                      color: 'white',
                      fontWeight: 'bold',
                      mb: 1
                    }}
                  />
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Status
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </motion.div>

      {/* Info Alert */}
      <motion.div variants={itemVariants}>
        <Alert
          severity="info"
          icon={<Info />}
          sx={{ mb: 4 }}
        >
          <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
            Why verify your score?
          </Typography>
          <Typography variant="body2">
            Verified scores are recognized by regulatory bodies, insurance companies, and real estate professionals.
            A verified compliance score adds credibility and can potentially reduce insurance premiums or increase property value.
          </Typography>
        </Alert>
      </motion.div>

      {/* Verification Options */}
      <motion.div variants={itemVariants}>
        <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', mb: 3 }}>
          Choose Your Verification Method
        </Typography>
      </motion.div>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        {verificationOptions.map((option) => (
          <Grid item xs={12} md={6} key={option.id}>
            <motion.div variants={itemVariants}>
              <Card
                sx={{
                  height: '100%',
                  cursor: 'pointer',
                  border: selectedOption === option.id ? '3px solid' : '1px solid',
                  borderColor: selectedOption === option.id ? 'primary.main' : 'grey.300',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 40px rgba(0,0,0,0.15)'
                  }
                }}
                onClick={() => setSelectedOption(option.id)}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                      {option.icon}
                    </Avatar>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {option.title}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                        <Chip
                          icon={<Schedule />}
                          label={option.duration}
                          size="small"
                          variant="outlined"
                        />
                        <Chip
                          icon={<AttachMoney />}
                          label={option.cost}
                          size="small"
                          color="secondary"
                        />
                      </Box>
                    </Box>
                    <Radio
                      checked={selectedOption === option.id}
                      onChange={() => setSelectedOption(option.id)}
                      sx={{ ml: 2 }}
                    />
                  </Box>

                  <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                    {option.description}
                  </Typography>

                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2 }}>
                    What's Included:
                  </Typography>

                  <List dense>
                    {option.features.map((feature, index) => (
                      <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 30 }}>
                          <CheckCircle sx={{ color: 'success.main', fontSize: 16 }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={feature}
                          sx={{ '& .MuiListItemText-primary': { fontSize: '0.85rem' } }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        ))}
      </Grid>

      {/* Additional Details Form */}
      {selectedOption && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          transition={{ duration: 0.3 }}
        >
          <Card sx={{ mb: 4 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3 }}>
                Additional Information
              </Typography>

              {selectedOption === 'visit' && (
                <Grid container spacing={3} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Preferred Date"
                      type="date"
                      value={preferredDate}
                      onChange={(e) => setPreferredDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ min: new Date().toISOString().split('T')[0] }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <FormLabel>Preferred Time</FormLabel>
                      <RadioGroup
                        row
                        value={preferredTime}
                        onChange={(e) => setPreferredTime(e.target.value)}
                      >
                        <FormControlLabel value="morning" control={<Radio />} label="Morning (9AM-12PM)" />
                        <FormControlLabel value="afternoon" control={<Radio />} label="Afternoon (1PM-5PM)" />
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                </Grid>
              )}

              <TextField
                fullWidth
                multiline
                rows={4}
                label="Additional Notes or Special Requirements"
                value={additionalNotes}
                onChange={(e) => setAdditionalNotes(e.target.value)}
                placeholder="Please provide any additional information that might help with the verification process..."
              />
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Submit Button */}
      {selectedOption && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Button
              variant="contained"
              size="large"
              onClick={handleSubmitVerification}
              disabled={selectedOption === 'visit' && (!preferredDate || !preferredTime)}
              sx={{
                px: 6,
                py: 2,
                fontSize: '1.1rem',
                background: 'linear-gradient(45deg, #1976d2, #42a5f5)',
                boxShadow: '0 8px 32px rgba(25, 118, 210, 0.3)'
              }}
            >
              Request {currentOption?.title} - {currentOption?.cost}
            </Button>
          </Box>
        </motion.div>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onClose={() => setShowConfirmDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <VerifiedUser sx={{ mr: 2, color: 'primary.main' }} />
            Confirm Verification Request
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            You've selected <strong>{currentOption?.title}</strong> for {currentOption?.cost}.
          </Typography>

          <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 2, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
              What happens next:
            </Typography>
            <List dense>
              <ListItem sx={{ px: 0 }}>
                <ListItemIcon>
                  <Timeline sx={{ color: 'primary.main' }} />
                </ListItemIcon>
                <ListItemText primary="We'll review your request and contact you within 24 hours" />
              </ListItem>
              {selectedOption === 'visit' && (
                <ListItem sx={{ px: 0 }}>
                  <ListItemIcon>
                    <Schedule sx={{ color: 'primary.main' }} />
                  </ListItemIcon>
                  <ListItemText primary="A certified assessor will schedule your property visit" />
                </ListItem>
              )}
              <ListItem sx={{ px: 0 }}>
                <ListItemIcon>
                  <CheckCircle sx={{ color: 'success.main' }} />
                </ListItemIcon>
                <ListItemText primary="You'll receive your verified compliance report" />
              </ListItem>
            </List>
          </Box>

          <Alert severity="info">
            Payment will be processed after verification is complete. You'll receive an invoice via email.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setShowConfirmDialog(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmSubmission}
            startIcon={<CheckCircle />}
          >
            Confirm Request
          </Button>
        </DialogActions>
      </Dialog>
    </motion.div>
  );
};

export default VerificationPage;